// src/pages/ForumsPage.js
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { subscribeToInboxMergedData, sendPlatformMessageData } from "../services/messagesDataService";

import {
  subscribeToForumPostsData,
  subscribeToForumBadgeData,
  createForumPostData,
  toggleForumSubscriptionData,
  uploadForumImagesData,
} from "../services/forumDataService";

import { getUserProfileData } from "../services/profileDataService";
import "./ForumsPage.css";
import GateIcon from "../components/GateIcon";
import OfflineBanner from "../components/OfflineBanner";
import { useAuth } from "../auth/AuthProvider";
import { isNativeMobileApp, makeNativeImageEntries } from "../utils/nativeImagePicker";
import ImageSourceModal from "../components/ImageSourceModal";
import SpeechIcon from "../components/SpeechIcon";
import ContentActionMenu from "../components/ContentActionMenu";
import { useBlockedUsers } from "../hooks/useBlockedUsers";

const MAX_IMAGES = 5;
const SEEN_REPLIES_KEY = "forum_seen_replies_v1";

const FORUM_BADGE_CACHE_KEY = "forum_badge_cache_v1";

const readForumBadgeCache = () => {
  try {
    return JSON.parse(localStorage.getItem(FORUM_BADGE_CACHE_KEY) || "{}");
  } catch {
    return {};
  }
};

const writeForumBadgeCache = (uid, data) => {
  try {
    const all = readForumBadgeCache();
    all[uid] = data;
    localStorage.setItem(FORUM_BADGE_CACHE_KEY, JSON.stringify(all));
  } catch {}
};

/** ✅ Abo: Union aus neuen + legacy Feldern */
const getSubscribersUnion = (postData) => {
  const a = Array.isArray(postData?.subscribers) ? postData.subscribers : [];
  const b = Array.isArray(postData?.subscribedBy) ? postData.subscribedBy : [];
  return Array.from(new Set([...(a || []), ...(b || [])]));
};

const readSeenRepliesMap = () => {
  try {
    return JSON.parse(localStorage.getItem(SEEN_REPLIES_KEY) || "{}");
  } catch {
    return {};
  }
};

const writeSeenRepliesMap = (map) => {
  try {
    localStorage.setItem(SEEN_REPLIES_KEY, JSON.stringify(map));
  } catch {}
};

const toForumMillis = (value) => {
  if (!value) return 0;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (value.seconds) {
    return value.seconds * 1000;
  }

  return 0;
};

const getPostActivityMillis = (post) => {
  const latestActivity = Math.max(
    toForumMillis(post?.lastActivityClientMs),
    toForumMillis(post?.lastActivityAt),
    toForumMillis(post?.lastReplyAt),
    toForumMillis(post?.lastActivityAtClient),
    toForumMillis(post?.lastReplyAtClient)
  );

  if (latestActivity > 0) return latestActivity;

  return Math.max(
    toForumMillis(post?.createdAt),
    toForumMillis(post?.createdAtClient)
  );
};

const formatForumDate = (post) => {
  const millis = getPostActivityMillis(post);
  if (!millis) return "";

  return new Date(millis).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};


const ForumsPage = () => {
  const navigate = useNavigate();

  // ✅ EINZIGE Auth-Quelle (für alles: unread + abos + actions)
  const { user } = useAuth();
  const myUid = user?.uid || null;
const isVerified = user?.emailVerified === true;
const {
  filterBlocked,
  addBlockedUser,
  isBlocked,
  blockedUserIds,
} = useBlockedUsers(myUid, isVerified);

  // ✅ Unread-Badge fürs Dropdown
  const [unreadCount, setUnreadCount] = useState(0);

  const [showCreatingPostModal, setShowCreatingPostModal] = useState(false);
const [creatingPostText, setCreatingPostText] = useState("");

  const [seenRepliesMap, setSeenRepliesMap] = useState(() => readSeenRepliesMap());
const cachedBadges = myUid ? readForumBadgeCache()[myUid] || {} : {};

const [myReplyActivityMap, setMyReplyActivityMap] = useState(
  () => cachedBadges.myReplies || {}
);

const [abosReplyActivityMap, setAbosReplyActivityMap] = useState(
  () => cachedBadges.abosReplies || {}
);
const handleOpenPost = (postId) => {
  const meta =
    myReplyActivityMap[postId] ||
    abosReplyActivityMap[postId] ||
    null;

  const currentSeen = Number(seenRepliesMap[postId] || 0);
  const ts = Math.max(currentSeen, Number(meta?.latestTs || 0), Date.now());

  setSeenRepliesMap((prev) => {
    const next = { ...prev, [postId]: ts };
    writeSeenRepliesMap(next);
    return next;
  });

  navigate(`/forum/beitrag/${postId}`);
};

 useEffect(() => {
  let unsub = null;
  let mounted = true;

  const initUnread = async () => {
    if (!myUid) {
      setUnreadCount(0);
      return;
    }

    try {
      unsub = await subscribeToInboxMergedData(myUid, (merged) => {
        if (!mounted) return;

        const list = Array.isArray(merged) ? merged : [];
       const c = list.reduce((acc, m) => {
  const senderId = m?.senderId || "";
  const isUnread = m?.recipientId === myUid && m?.read === false;

  if (!isUnread) return acc;

  // ✅ Nachrichten von blockierten Nutzern nicht zählen
  if (senderId && isBlocked(senderId)) return acc;

  return acc + 1;
}, 0);

        setUnreadCount(c);
      });
    } catch (e) {
      if (mounted) setUnreadCount(0);
    }
  };

  initUnread();

  return () => {
    mounted = false;
    if (typeof unsub === "function") unsub();
  };
}, [myUid, isBlocked, blockedUserIds]);

useEffect(() => {
  let unsub = null;
  let mounted = true;

  const initBadges = async () => {
    if (!myUid) {
      setMyReplyActivityMap({});
      setAbosReplyActivityMap({});
      return;
    }

    try {
      unsub = await subscribeToForumBadgeData(myUid, ({ myReplies, abosReplies }) => {
        if (!mounted) return;

       const cached = readForumBadgeCache()[myUid] || {};

const nextMyReplies =
  myReplies && Object.keys(myReplies).length > 0
    ? myReplies
    : cached.myReplies || {};

const nextAbosReplies =
  abosReplies && Object.keys(abosReplies).length > 0
    ? abosReplies
    : cached.abosReplies || {};

setMyReplyActivityMap(nextMyReplies);
setAbosReplyActivityMap(nextAbosReplies);

if (
  Object.keys(nextMyReplies).length > 0 ||
  Object.keys(nextAbosReplies).length > 0
) {
  writeForumBadgeCache(myUid, {
    myReplies: nextMyReplies,
    abosReplies: nextAbosReplies,
  });
}

      });
    } catch (err) {
      console.warn("ForumsPage: subscribeToForumBadgeData fehlgeschlagen:", err);

  
    }
  };

  initBadges();
  

  return () => {
    mounted = false;
    if (typeof unsub === "function") unsub();
  };
}, [myUid]);

  const [posts, setPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [modalRecipient, setModalRecipient] = useState(null); // { uid, name }
  const [newMessageText, setNewMessageText] = useState("");
  const [subscriptionMessage, setSubscriptionMessage] = useState("");
  const [newPostVisible, setNewPostVisible] = useState(false);

  // 🔹 Login-Pflicht-Popup
  const [showLoginRequiredModal, setShowLoginRequiredModal] = useState(false);

  // ✅ Generisches EE-Info Popup (ersetzt alle alerts)
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalTitle, setInfoModalTitle] = useState("");
  const [infoModalText, setInfoModalText] = useState("");

  const openInfoModal = (title, text) => {
    setInfoModalTitle(title || "Hinweis");
    setInfoModalText(text || "");
    setShowInfoModal(true);
  };

  const closeInfoModal = () => setShowInfoModal(false);

  // Bilder für neuen Beitrag: [{ file, preview }]
  const [images, setImages] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(null);

  const [newPostTitle, setNewPostTitle] = useState("");
  const [newPostContent, setNewPostContent] = useState("");

  // ✅ Kategorie beim Erstellen eines Beitrags
  const [newPostCategory, setNewPostCategory] = useState("");

  // ✅ Suche + Kategorie-Filter für bestehende Beiträge
  const [searchQueryText, setSearchQueryText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // Lade- und Fehlerzustände
  const [isLoading, setIsLoading] = useState(true);
  const [postsError, setPostsError] = useState("");
  const [showImageLimitPopup, setShowImageLimitPopup] = useState(false);
const [showImageSourceModal, setShowImageSourceModal] = useState(false);

  const fileInputRef = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // ✅ Einheitlicher Trigger wie ProfilPage (kein capture, value reset)
 const triggerImagePicker = (inputRef) => {
  if (isNativeMobileApp()) {
    setShowImageSourceModal(true);
    return;
  }

  const el = inputRef?.current;
  if (!el) return;

  el.value = null;
  el.removeAttribute("capture");
  el.click();
};

  // 🌐 OfflineBanner-Status (nur Anzeige)
  const [isOffline, setIsOffline] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  // Pagination
  const postsPerPage = 20;

  // ✅ Filter anwenden (Suche + Kategorie)
  const normalizedQuery = (searchQueryText || "").trim().toLowerCase();
 const visiblePosts = filterBlocked(posts);

const filteredPosts = visiblePosts
  .filter((p) => {
    const title = (p?.title || "").toString().toLowerCase();
    const content = (p?.content || "").toString().toLowerCase();
    const author = (p?.authorName || p?.author || "").toString().toLowerCase();

    const matchesText =
      !normalizedQuery ||
      title.includes(normalizedQuery) ||
      content.includes(normalizedQuery) ||
      author.includes(normalizedQuery);

    const postCat = (p?.category || "").toString();
    const matchesCategory = !categoryFilter || postCat === categoryFilter;

    return matchesText && matchesCategory;
  })
  .sort((a, b) => getPostActivityMillis(b) - getPostActivityMillis(a));

  // ✅ Pagination auf Basis der gefilterten Posts
  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / postsPerPage || 1));
  const pagedPosts = filteredPosts.slice(
    (currentPage - 1) * postsPerPage,
    currentPage * postsPerPage
  );

  // ✅ Wenn Filter sich ändern: zurück auf Seite 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQueryText, categoryFilter]);

  // 🔍 Verbindung für OfflineBanner prüfen
  useEffect(() => {
    const checkConnection = async () => {
      try {
        await fetch("https://www.gstatic.com/firebasejs/9.6.1/firebase-app.js", {
          method: "HEAD",
          cache: "no-store",
        });
        setIsOffline(false);
      } catch (err) {
        console.warn("Keine Server-Verbindung möglich (ForumsPage):", err);
        setIsOffline(true);
      }
    };
    checkConnection();
  }, [retryAttempt]);

  const handleOfflineRetry = () => {
    setIsOffline(false);
    setTimeout(() => setRetryAttempt((prev) => prev + 1), 600);
  };

  useEffect(() => {
  let unsub = null;
  let mounted = true;

  const initPosts = async () => {
    setIsLoading(true);
    setPostsError("");

    try {
      unsub = await subscribeToForumPostsData(
        (list) => {
          if (!mounted) return;
          setPosts(Array.isArray(list) ? list : []);
          setIsLoading(false);
        },
        (err) => {
          console.warn("⚠️ Fehler beim Laden der forumPosts:", err);
          if (!mounted) return;
          setPostsError("Die Forumsbeiträge konnten aktuell nicht geladen werden.");
          setIsLoading(false);
        }
      );
    } catch (err) {
      console.warn("⚠️ subscribeToForumPostsData Fehler:", err);
      if (!mounted) return;
      setPostsError("Die Forumsbeiträge konnten aktuell nicht geladen werden.");
      setIsLoading(false);
    }
  };

  initPosts();

  return () => {
    mounted = false;
    if (typeof unsub === "function") unsub();
  };
}, []);
 const countUnreadReplies = (activityMap) => {
  return Object.values(activityMap).reduce((acc, meta) => {
    const postId = meta?.postId;
    const latestTs = Number(meta?.latestTs || 0);
    const lastSeen = Number(seenRepliesMap[postId] || 0);
    const latestAuthorId = meta?.latestAuthorId || "";

    if (!postId) return acc;
    if (!latestTs || latestTs <= lastSeen) return acc;
    if (!latestAuthorId || latestAuthorId === myUid) return acc;

    // ✅ Antworten von blockierten Nutzern nicht zählen
    if (isBlocked(latestAuthorId)) return acc;

    return acc + 1;
  }, 0);
};

const myUnreadReplyCount = countUnreadReplies(myReplyActivityMap);
const abosUnreadReplyCount = countUnreadReplies(abosReplyActivityMap);
const dropdownKey = JSON.stringify({
  uid: myUid || "guest",
  my: myUnreadReplyCount,
  abos: abosUnreadReplyCount,
  msg: unreadCount,
  myKeys: Object.keys(myReplyActivityMap).join("|"),
  aboKeys: Object.keys(abosReplyActivityMap).join("|"),
});


  // ✅ pro Post: ist dieser User abonniert? (Union: subscribers + subscribedBy)
  const isSubscribedByMe = (post) => {
    if (!myUid) return false;
    return getSubscribersUnion(post).includes(myUid);
  };

  // 🔹 Dropdown: Meine / Abos / Nachrichten nur bei Login
  const handleDropdownChange = (e) => {
    const value = e.target.value;
    const isLoggedIn = !!myUid && isVerified;

    if (value && !isLoggedIn) {
      setShowLoginRequiredModal(true);
      e.target.value = "";
      return;
    }

    navigate(value ? `/forum/${value}` : "/forum");
  };

  // 🔹 Neues Thema erstellen nur bei Login
  const handleNewPostClick = () => {
    const isLoggedIn = !!myUid && isVerified;
    if (!isLoggedIn) {
      setShowLoginRequiredModal(true);
      return;
    }
    setNewPostVisible((p) => !p);
  };

  // Bilderupload – max. 5
 const handleImageUpload = async (e, source = "gallery") => {
  const remainingSlots = MAX_IMAGES - (images?.length || 0);
  if (remainingSlots <= 0) {
    setShowImageLimitPopup(true);
    return;
  }

  let newEntries = [];

  if (isNativeMobileApp() && (!e?.target?.files || e.target.files.length === 0)) {
  try {
    newEntries = await makeNativeImageEntries({
      prefix: "forum-post",
      source,
      limit: remainingSlots,
    });
  } catch (err) {
    console.warn("Native Bildauswahl abgebrochen:", err);
    return;
  }

  } else {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const validFiles = files.slice(0, remainingSlots);
    newEntries = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
  }

  setImages((prev) => {
  const updated = [...prev, ...newEntries];

  if (mainImageIndex === null && updated.length > 0) {
    setMainImageIndex(0);
  }

  return updated;
});

  if (e?.target) e.target.value = null;
};
const handleChooseCamera = () => {
  setShowImageSourceModal(false);

  requestAnimationFrame(() => {
    handleImageUpload({}, "camera");
  });
};

const handleChooseGallery = () => {
  setShowImageSourceModal(false);

  requestAnimationFrame(() => {
    handleImageUpload({}, "gallery");
  });
};

  const handleDeleteImage = (index) => {
    setImages((prev) => {
      const target = prev?.[index];
      try {
        if (target?.preview) URL.revokeObjectURL(target.preview);
      } catch {}

      const updated = prev.filter((_, i) => i !== index);

      let newMainIndex = mainImageIndex;
      if (mainImageIndex === index) {
        newMainIndex = updated.length > 0 ? 0 : null;
      } else if (mainImageIndex > index) {
        newMainIndex = mainImageIndex - 1;
      }

      setMainImageIndex(newMainIndex);
      return updated;
    });
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
  };

  const handleDrop = (e, index) => {
    const from = dragItem.current;
    if (from === null || from === index) return;

    const copied = [...images];
    const draggedItem = copied[from];
    copied.splice(from, 1);
    copied.splice(index, 0, draggedItem);

    setImages(copied);

    setMainImageIndex((prev) => {
      if (prev === null) return prev;
      if (prev === from) return index;
      if (prev === index) return from;
      if (from < prev && index >= prev) return prev - 1;
      if (from > prev && index <= prev) return prev + 1;
      return prev;
    });

    dragItem.current = null;
    dragOverItem.current = null;
  };

  // ✅ Abo (🔔) speichern: schreibt IMMER beide Felder (subscribers + subscribedBy)
  const toggleSubscription = async (postId, currentlySubscribed) => {
    if (!myUid || !isVerified) {
      setShowLoginRequiredModal(true);
      return;
    }

    const newStatus = !currentlySubscribed;

    // UI sofort: lokales Update
    setPosts((prev) =>
      prev.map((p) => {
        if (p.id !== postId) return p;

        const union = getSubscribersUnion(p);
        const next = newStatus
          ? Array.from(new Set([...union, myUid]))
          : union.filter((x) => x !== myUid);

        return {
          ...p,
          subscribers: next,
          subscribedBy: next,
        };
      })
    );

   try {
  await toggleForumSubscriptionData(postId, myUid, newStatus);

  setSubscriptionMessage(
    newStatus ? "Beitrag wurde abonniert." : "Beitrag nicht mehr abonniert."
  );
  setTimeout(() => setSubscriptionMessage(""), 2500);
} catch (err) {
  console.warn("⚠️ Abo konnte nicht gespeichert werden:", err);
  setSubscriptionMessage("Abo konnte nicht gespeichert werden.");
  setTimeout(() => setSubscriptionMessage(""), 2500);
}
  };

  // 🔹 Nachricht an Autor nur bei Login
  const handleUserClick = (recipient, e) => {
    e.stopPropagation();
    const isLoggedIn = !!myUid && isVerified;

    if (!isLoggedIn) {
      setShowLoginRequiredModal(true);
      return;
    }
    if (!recipient?.uid) {
      openInfoModal("Nicht möglich", "Dieser Beitrag hat keinen gültigen Autor (userId fehlt).");
      return;
    }

    setModalRecipient(recipient);
    setShowMessageModal(true);
  };

  // 🔥 Plattform-Nachricht speichern
  const handleSendMessage = async () => {
    const text = newMessageText.trim();
    if (!text) {
      openInfoModal("Fehlende Nachricht", "Bitte gib einen Nachrichtentext ein.");
      return;
    }

    if (!myUid || !isVerified) {
      setShowMessageModal(false);
      setShowLoginRequiredModal(true);
      return;
    }

    if (!modalRecipient?.uid) {
      openInfoModal("Nicht möglich", "Kein gültiger Empfänger ausgewählt.");
      return;
    }

   const senderEmail = myUid ? (user?.email || "") : "";
    const senderName = myUid ? (user?.displayName || "") : "";

    try {
      await sendPlatformMessageData({
        senderId: myUid,
        recipientId: modalRecipient.uid,
        senderEmail,
        recipientEmail: "",
        senderName,
        recipientName: modalRecipient.name || "",
        text,
        contextType: "forumPost",
        contextId: null,
      });
    } catch (error) {
      console.warn("⚠️ Nachricht konnte nicht gespeichert werden:", error);
      openInfoModal("Fehler", "Nachricht konnte nicht gespeichert werden. Bitte prüfe die Verbindung.");
    }

    setNewMessageText("");
    setShowMessageModal(false);
  };

  // Bilder in Firebase Storage hochladen und URLs zurückgeben
 const uploadPostImagesToStorage = async (userId) => {
  if (!images.length) return [];

  const files = images
    .map((entry) => entry?.file)
    .filter((file) => file instanceof File || file instanceof Blob);

  if (!files.length) return [];

  return await uploadForumImagesData(files, `forumPosts/${userId}/posts`);
};

  // ✅ Autorname aus Profil holen (nutzer/{uid}.name)
  const getAuthorNameFromProfile = async (uid) => {
  try {
    const profile = await getUserProfileData(uid);
    return String(profile?.name || "").trim();
  } catch (err) {
    console.warn("⚠️ Konnte Profilname nicht laden:", err);
    return "";
  }
};

  const handleCreatePost = async () => {
    if (newPostTitle.trim() === "" || newPostContent.trim() === "") {
      openInfoModal("Fehlende Angaben", "Titel und Beschreibung dürfen nicht leer sein.");
      return;
    }

    // ✅ Kategorie Pflicht
    if (!newPostCategory || String(newPostCategory).trim() === "") {
      openInfoModal("Kategorie erforderlich", "Bitte wähle eine Kategorie, bevor du veröffentlichst.");
      return;
    }

    // ✅ Konsistent: nur myUid/user
    if (!myUid || !isVerified) {
      setShowLoginRequiredModal(true);
      return;
    }

    const userId = myUid;

    setShowCreatingPostModal(true);
setCreatingPostText(
  images.length > 0
    ? "Beitrag wird erstellt. Der Bilder-Upload kann einen Moment dauern…"
    : "Beitrag wird erstellt…"
);

    try {
      const profileName = await getAuthorNameFromProfile(userId);
      const authorName =
        profileName ||
        user?.displayName ||
        (user?.email ? user.email.split("@")[0] : "") ||
        "Unbekannt";

      const imageUrls = await uploadPostImagesToStorage(userId);

      const mainIndex =
        mainImageIndex !== null ? mainImageIndex : imageUrls.length > 0 ? 0 : null;

      const post = {
  title: newPostTitle,
  content: newPostContent,

  // ✅ beide Felder speichern, damit alle Seiten/Rechte sauber funktionieren
  userId,
  authorId: userId,

  authorName,
  category: newPostCategory,
  createdAtClient: Date.now(),
  updatedAtClient: Date.now(),
  images: imageUrls,
  mainImageIndex: mainIndex,
  subscribers: [],
  subscribedBy: [],
  subscribed: false,
};

      await createForumPostData(post);
      setShowCreatingPostModal(false);

      // UI reset
      setNewPostTitle("");
      setNewPostContent("");
      setNewPostCategory("");

      images.forEach((img) => {
        try {
          if (img?.preview) URL.revokeObjectURL(img.preview);
        } catch {}
      });
      setImages([]);
      setMainImageIndex(null);
      setNewPostVisible(false);
    } catch (error) {
  console.warn("⚠️ Fehler beim Erstellen des Posts:", error);
  setShowCreatingPostModal(false);
  openInfoModal("Fehler", "Beitrag konnte nicht erstellt werden. Bitte prüfe die Verbindung.");
}
  };

  return (
    <div className="forum-container">
      {/* 🔥 Offline-Banner */}
      {isOffline && <OfflineBanner onRetry={handleOfflineRetry} />}

      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Logo"
        className="browse-logo"
        onClick={() => navigate("/")}
      />
      <GateIcon className="login-icon" />
<SpeechIcon className="speech-icon no-tap-highlight" />
<img
  src="/rezept-icon.png"
  alt="Rezepthelfer"
  className="rezept-icon"
  onClick={() => navigate("/rezepte")}
/>
      <p className="top-claim">Zusammen wachsen – im Forum!</p>

      <div className="forum-main-box">
        {subscriptionMessage && (
  <div className="mainbox-toast">
    {subscriptionMessage}
  </div>
)}
        <div className="forum-dropdown">
          <div className="custom-select-wrapper" key={`forum-dropdown-${dropdownKey}`}>
          <select
           value=""
           onChange={handleDropdownChange}
           key={`forum-select-${dropdownKey}`}
         >

  <option value="">Alle Beiträge</option>
  <option value="my">
  {myUnreadReplyCount > 0 ? "🔴 " : ""}Meine Beiträge
</option>

<option value="abos">
  {abosUnreadReplyCount > 0 ? "🔴 " : ""}Abonnierte Beiträge
</option>

<option value="messages">
  {unreadCount > 0 ? "🔴 " : ""}Nachrichten
</option>
</select>
            <span className="arrow-down">▼</span>
          </div>
        </div>

        <button className="forums-green-button" onClick={handleNewPostClick}>
          + Neues Thema erstellen
        </button>

        {/* ✅ Suche + Kategorie-Filter nur anzeigen, wenn Create-Form geschlossen ist */}
        {!newPostVisible && (
          <div className="forum-filter-bar">
            <input
              type="text"
              className="forum-search-input"
              placeholder="Beiträge durchsuchen..."
              value={searchQueryText}
              onChange={(e) => setSearchQueryText(e.target.value)}
            />

            <div className="forum-category-filter">
              <select
                className="forum-category-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Alle Kategorien</option>
                <option value="garten">Rund um den Garten</option>
                <option value="kueche">Rund um die Küche</option>
                <option value="rezepte">Diese Rezepte sind klasse!</option>
              </select>
              <span className="arrow-down">▼</span>
            </div>
          </div>
        )}

        {newPostVisible && (
          <div className="new-post-fields">
            <input
              type="text"
              placeholder="Thema"
              className="new-post-title"
              value={newPostTitle}
              onChange={(e) => setNewPostTitle(e.target.value)}
            />

            <div className="upload-textarea-wrapper">
              <textarea
                placeholder="Beschreibung"
                className="new-post-description"
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
              />
              {images.length === 0 && (
            <img
              src="/upload-icon.png"
              alt="Upload"
              className="upload-icon-small"
              onClick={() => triggerImagePicker(fileInputRef)}
              />  
              )}
            </div>

            {/* ✅ Kategorie-Auswahl für neuen Beitrag */}
            <div className="new-post-category-wrapper">
              <select
                className="new-post-category-select"
                value={newPostCategory}
                onChange={(e) => setNewPostCategory(e.target.value)}
              >
                <option value="">Kategorie wählen...</option>
                <option value="garten">Rund um den Garten</option>
                <option value="kueche">Rund um die Küche</option>
                <option value="rezepte">Diese Rezepte sind klasse!</option>
              </select>
              <span className="arrow-down">▼</span>
            </div>

            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              ref={fileInputRef}
              style={{ display: "none" }}
            />

            <div className="image-preview-row">
  {images.map((img, i) => (
    <div
      key={i}
      className={`image-item ${mainImageIndex === i ? "main-image" : ""}`}
      draggable
      onDragStart={(e) => handleDragStart(e, i)}
      onDragOver={(e) => {
        e.preventDefault();
        dragOverItem.current = i;
      }}
      onDrop={(e) => handleDrop(e, i)}
    >
      <img src={img.preview} alt={`preview-${i}`} className="preview-image" />
      <div className="image-actions">
        <button type="button" onClick={() => handleDeleteImage(i)}>
          X
        </button>
        <button type="button" onClick={() => setMainImageIndex(i)}>
          ★
        </button>
      </div>
    </div>
  ))}

  {images.length > 0 && images.length < MAX_IMAGES && (
    <div
      className="forum-preview-add-image-item"
      onClick={() => triggerImagePicker(fileInputRef)}
    >
      <img src="/upload-icon.png" alt="Weiteres Bild hinzufügen" />
    </div>
  )}
</div>

            <button
  className="green-button"
  onClick={handleCreatePost}
  disabled={showCreatingPostModal}
>
  {showCreatingPostModal ? "Wird erstellt…" : "Veröffentlichen"}
</button>

            {/* ✅ Suche + Kategorie-Filter rutschen nach unten, wenn Create-Form offen ist */}
            <div className="forum-filter-bar" style={{ marginTop: 10 }}>
              <input
                type="text"
                className="forum-search-input"
                placeholder="Beiträge durchsuchen..."
                value={searchQueryText}
                onChange={(e) => setSearchQueryText(e.target.value)}
              />

              <div className="forum-category-filter">
                <select
                  className="forum-category-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">Alle Kategorien</option>
                  <option value="garten">Rund um den Garten</option>
                  <option value="kueche">Rund um die Küche</option>
                  <option value="rezepte">Diese Rezepte sind klasse!</option>
                </select>
                <span className="arrow-down">▼</span>
              </div>
            </div>

            {showImageLimitPopup && (
              <div className="modal-overlay" onClick={() => setShowImageLimitPopup(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <p>Maximal {MAX_IMAGES} Bilder erlaubt.</p>
                  <button className="green-button" onClick={() => setShowImageLimitPopup(false)}>
                    OK
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {postsError && <div className="error-message forum-error">{postsError}</div>}
        {isLoading && posts.length === 0 && <p>Lade Beiträge...</p>}

        {pagedPosts.map((post) => {
          const subscribed = isSubscribedByMe(post);
          const authorLabel = post.authorName || post.author || "Unbekannt";

          const ownMeta = myReplyActivityMap[post.id];
const aboMeta = abosReplyActivityMap[post.id];

const lastSeen = Number(seenRepliesMap[post.id] || 0);

const hasOwnUnreadReply =
  Number(ownMeta?.latestTs || 0) > lastSeen &&
  ownMeta?.latestAuthorId &&
  ownMeta.latestAuthorId !== myUid &&
  !isBlocked(ownMeta.latestAuthorId);

const hasAboUnreadReply =
  Number(aboMeta?.latestTs || 0) > lastSeen &&
  aboMeta?.latestAuthorId &&
  aboMeta.latestAuthorId !== myUid &&
  !isBlocked(aboMeta.latestAuthorId);

const hasUnreadReply = hasOwnUnreadReply || hasAboUnreadReply;
            
 return (
  <div className="message-row" key={post.id}>
    
    {hasUnreadReply && <span className="unread-icon">❗</span>}

    <div
  className="post-box compact-post hoverable"
  onClick={() => handleOpenPost(post.id)}
>
  <div className="compact-post-topline">
  <span className="compact-post-date">
    Erstellt am {formatForumDate(post)}
  </span>
</div>

<div
  className="compact-post-actions"
  onClick={(e) => e.stopPropagation()}
>
  <span
    className="bell-icon clickable"
    onClick={(e) => {
      e.stopPropagation();
      toggleSubscription(post.id, subscribed);
    }}
    title={subscribed ? "Abo entfernen" : "Beitrag abonnieren"}
  >
    {subscribed ? "🔔" : "🔕"}
  </span>

  <ContentActionMenu
  contentType="forumPost"
  contentId={post.id}
  ownerId={post.userId || post.authorId || post.ownerId || null}
  ownerName={authorLabel}

  contentTitle={post.title || "Beitrag bei Edens Ernte"}
  contentText={post.content || ""}

  currentUser={user}
  shareLabel="Beitrag teilen"
  reportLabel="Beitrag melden"
  blockLabel="Nutzer blockieren"
  shareTitle={post.title || "Beitrag bei Edens Ernte"}
  shareText={post.content || ""}
  shareUrl={`/forum/beitrag/${post.id}`}
  onLoginRequired={() => setShowLoginRequiredModal(true)}
  onActionToast={(text) => {
    setSubscriptionMessage(text);
    setTimeout(() => setSubscriptionMessage(""), 2500);
  }}
  onBlockedUser={(blockedUserId) => {
    addBlockedUser(blockedUserId);
  }}
/>
</div>

  <h3>{post.title}</h3>
  <p>{post.content}</p>

  <span
    className="author-bottom-right clickable"
    onClick={(e) =>
      handleUserClick(
  {
    uid: post.userId || post.authorId || post.ownerId || null,
    name: authorLabel,
  },
  e
)
    }
    title="Nachricht schreiben"
  >
    {authorLabel}
  </span>
</div>

  </div>
);
          
        })}

        {totalPages > 1 && (
          <div className="pagination">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              ⏮
            </button>
            <button onClick={() => setCurrentPage((prev) => Math.max(prev - 10, 1))}>«</button>
            <button onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>‹</button>
            <span>
              {currentPage} / {totalPages}
            </span>
            <button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}>›</button>
            <button onClick={() => setCurrentPage((prev) => Math.min(prev + 10, totalPages))}>»</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
              ⏭
            </button>
          </div>
        )}
      </div>

      

      {showMessageModal && (
        <div className="footer-modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="footer-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Nachricht an {modalRecipient?.name || "Nutzer"}</h3>
            <textarea
              placeholder="Nachricht schreiben..."
              value={newMessageText}
              onChange={(e) => setNewMessageText(e.target.value)}
              rows="4"
              style={{ width: "100%", maxHeight: "120px", marginBottom: "12px" }}
            />
            <button className="green-button" onClick={handleSendMessage}>
              Nachricht senden
            </button>
            <button
              className="red-button"
              onClick={() => setShowMessageModal(false)}
              style={{ marginTop: "10px" }}
            >
              Schließen
            </button>
          </div>
        </div>
      )}

      {/* 🔹 Login erforderlich – Edens-Ernte-Style */}
      {showLoginRequiredModal && (
        <div className="modal-overlay" onClick={() => setShowLoginRequiredModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Login erforderlich</h3>
            <p>Um diese Funktion nutzen zu können, musst du bei Edens Ernte eingeloggt sein.</p>
            <div className="modal-buttons">
              <button
                className="green-button"
                onClick={() => {
                  setShowLoginRequiredModal(false);
                  navigate("/login");
                }}
              >
                Zum Login
              </button>
              <button className="red-button" onClick={() => setShowLoginRequiredModal(false)}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ GENERISCHES EE-INFO POPUP */}
      {showInfoModal && (
        <div className="modal-overlay" onClick={closeInfoModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{infoModalTitle}</h3>
            <p>{infoModalText}</p>
            <div className="modal-buttons">
              <button className="green-button" onClick={closeInfoModal}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {showCreatingPostModal && (
  <div className="modal-overlay">
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <h3>Beitrag wird erstellt</h3>
      <p>{creatingPostText}</p>
      <div className="ee-loading-spinner"></div>
    </div>
  </div>
)}
      <ImageSourceModal
  isOpen={showImageSourceModal}
  onClose={() => setShowImageSourceModal(false)}
  onCamera={handleChooseCamera}
  onGallery={handleChooseGallery}
/>
    </div>
  );
};

export default ForumsPage;
